import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ClientActions } from '@app/core/state/clients/clients.actions';
import { ClientSelectors } from '@app/core/state/clients/clients.selectors';
import { ClientType } from '@app/core/state/types';
import { BlFrontendButtonComponent } from '@app/core/wrappers/bl-frontend-button.component';
import { DialogData, DialogResult } from '@app/shared/components/form-dialog-base/form-dialog-base.component';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { skipWhile } from 'rxjs';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableFormDialogBaseComponent } from 'src/app/core/i18n/translatable-form-dialog-base.component';
import { TranslatableString } from 'src/app/core/i18n/TranslatableString';
import { TranslatableStringType } from 'src/app/core/i18n/types';

interface Data extends DialogData {
  client: ClientType;
}

interface Result extends DialogResult {
  client: ClientType | null;
}

const TRANSLATION_SCOPE = 'archiveOrDeleteClientDialogComponent';

@Component({
  selector: 'app-archive-or-delete-client-dialog',
  template: `
    <div class="flex flex-row justify-between mt-6">
      <div>
        <bl-frontend-button
          [text]="'shared.archive' | transloco"
          brand="firmsupport"
          (onclick)="onArchive()"></bl-frontend-button>
      </div>
      <div>
        <bl-frontend-button
          class="mr-2"
          [text]="'shared.cancel' | transloco"
          variant="secondary"
          (onclick)="close({ client: null })"></bl-frontend-button>
        <bl-frontend-button
          [text]="'shared.deletePermanently' | transloco"
          variant="primary-negative"
          (onclick)="onDelete()"></bl-frontend-button>
      </div>
    </div>
  `,
  standalone: true,
  imports: [CommonModule, BlFrontendButtonComponent, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [getTranslationProvider(TRANSLATION_SCOPE)],
})
export class ArchiveOrDeleteClientDialogComponent extends TranslatableFormDialogBaseComponent<Data, Result> {
  constructor(
    protected override ref: DynamicDialogRef,
    protected override config: DynamicDialogConfig<Data>,
  ) {
    super(ref, config);
    this.config.header = (this.config.header as TranslatableStringType).withParams({
      clientName: this.config.data.client.name,
    });

    this.store
      .select(ClientSelectors.clientStateArchiving)
      .pipe(
        skipWhile((value) => !value),
        skipWhile((value) => value),
        this.takeUntilDestroyed(),
      )
      .subscribe(() => {
        const { client } = this.config.data;
        this.close({ client: { ...client, archived: true } });
      });

    this.store
      .select(ClientSelectors.clientStateDeleting)
      .pipe(
        skipWhile((value) => !value),
        skipWhile((value) => value),
        this.takeUntilDestroyed(),
      )
      .subscribe(() => {
        const { client } = this.config.data;
        this.close({ client: { ...client } });
      });
  }

  onDelete() {
    const { client } = this.config.data;
    this.store.dispatch(
      ClientActions.deleteClient({ client: { ...client }, predicate: (item) => item.id !== client.id }),
    );
  }

  onArchive() {
    const { client } = this.config.data;
    this.store.dispatch(
      ClientActions.archiveDeletableClient({
        client: { ...client, archived: true },
        predicate: (item) => item.id === client.id,
      }),
    );
  }

  protected static override getTranslationScope(): string {
    return TRANSLATION_SCOPE;
  }

  protected static override getDialogConfig(
    translation: Record<string, string>,
  ): Omit<DynamicDialogConfig<Data>, 'data'> {
    return {
      header: new TranslatableString(translation.whatToDoWith) as unknown as string,
      modal: true,
      dismissableMask: true,
      draggable: false,
      width: '800px',
    };
  }
}
